import { usePhotoGallery, type PhotoOptions, type PhotoResult } from './usePhotoGallery';
import { useErrorToast } from './useErrorToast';
import { UnsupportedImageError } from '../utils/normalizeImage';

const isUserCancellation = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return /cancel/i.test(msg) || msg === 'No image picked';
};

/**
 * Picks a photo from the camera or the gallery and hands it to `onPicked`.
 * Cancelling the picker is silent; a format the app cannot read shows its own
 * message, and any other picker error a generic toast.
 * Errors thrown by `onPicked` itself are the caller's responsibility.
 * `options` override the picker defaults (e.g. a larger size than avatars).
 */
export const usePhotoPicker = (options?: PhotoOptions) => {
  const { takePhoto, chooseFromGallery } = usePhotoGallery(options);
  const showError = useErrorToast();

  const pick = async (
    source: () => Promise<PhotoResult>,
    errorMessage: string,
    onPicked: (photo: PhotoResult) => Promise<void> | void
  ) => {
    let photo: PhotoResult;
    try {
      photo = await source();
    } catch (error) {
      if (error instanceof UnsupportedImageError) showError(error.message);
      else if (!isUserCancellation(error)) showError(errorMessage);
      return;
    }
    await onPicked(photo);
  };

  const pickFromCamera = (onPicked: (photo: PhotoResult) => Promise<void> | void) =>
    pick(takePhoto, 'No se pudo acceder a la cámara.', onPicked);

  const pickFromGallery = (onPicked: (photo: PhotoResult) => Promise<void> | void) =>
    pick(chooseFromGallery, 'No se pudo acceder a la galería.', onPicked);

  return { pickFromCamera, pickFromGallery };
};
